<template>
  <div v-if="isAdmin" class="subscription-plans-admin-page" :class="{ 'twitch-theme': isTwitchUser }">
    <card class="subscription-plans-card">
      <div slot="header" class="d-flex flex-wrap align-items-center justify-content-between subscription-plans-header">
        <div>
          <h4 class="card-title mb-1">{{ tt("subscriptionPlansAdmin.title", "Planes de suscripción") }}</h4>
          <p class="subscription-plans-subtitle mb-0">
            {{ tt("subscriptionPlansAdmin.subtitle", "Administra catálogo, disponibilidad y versiones históricas de planes.") }}
          </p>
        </div>
        <div class="subscription-plans-header-actions d-flex flex-wrap align-items-center mt-2 mt-md-0">
          <button class="btn btn-outline-primary btn-sm btn-round" :disabled="loading || saving" @click="openCreateModal">
            <i class="ti-plus mr-1"></i>
            {{ tt("subscriptionPlansAdmin.create", "Nuevo plan") }}
          </button>
          <button class="btn btn-primary btn-sm btn-round" :disabled="loading" @click="loadPlans">
            <i class="ti-reload mr-1" :class="{ 'ti-spin': loading }"></i>
            {{ loading ? tt("subscriptionPlansAdmin.reloading", "Recargando...") : tt("subscriptionPlansAdmin.reload", "Recargar") }}
          </button>
        </div>
      </div>

      <div slot="raw-content" class="subscription-plans-body">
        <div v-if="errorMessage" class="alert alert-danger mb-3">
          {{ errorMessage }}
        </div>

        <div v-if="successMessage" class="alert alert-success mb-3">
          {{ successMessage }}
        </div>

        <div v-if="!loading && groupedPlans.length > 0" class="subscription-plans-filters">
          <div class="subscription-plans-filter-group">
            <div class="subscription-plans-filters-label">
              {{ tt("subscriptionPlansAdmin.filterByBillingCycle", "Filtrar por ciclo de facturación") }}
            </div>
            <div class="subscription-plans-filter-tabs" role="tablist" :aria-label="tt('subscriptionPlansAdmin.filterTabsAria', 'Filtrar planes por período')">
              <button
                type="button"
                class="subscription-plans-filter-tab"
                :class="{ active: activePeriodFilter === 'all' }"
                :aria-pressed="activePeriodFilter === 'all'"
                @click="activePeriodFilter = 'all'"
              >
                {{ tt("subscriptionPlansAdmin.filterAll", "Todos") }}
              </button>
              <button
                type="button"
                class="subscription-plans-filter-tab"
                :class="{ active: activePeriodFilter === 'monthly' }"
                :aria-pressed="activePeriodFilter === 'monthly'"
                @click="activePeriodFilter = 'monthly'"
              >
                {{ tt("subscriptionPlansAdmin.filterMonthly", "Mensuales") }}
              </button>
              <button
                type="button"
                class="subscription-plans-filter-tab"
                :class="{ active: activePeriodFilter === 'yearly' }"
                :aria-pressed="activePeriodFilter === 'yearly'"
                @click="activePeriodFilter = 'yearly'"
              >
                {{ tt("subscriptionPlansAdmin.filterYearly", "Anuales") }}
              </button>
            </div>
          </div>

          <div class="subscription-plans-filter-group">
            <div class="subscription-plans-filters-label">
              {{ tt("subscriptionPlansAdmin.filterByCheckoutVisibility", "Filtrar por visibilidad en checkout") }}
            </div>
            <div class="subscription-plans-filter-tabs" role="tablist" :aria-label="tt('subscriptionPlansAdmin.visibilityTabsAria', 'Filtrar planes por visibilidad en checkout')">
              <button
                type="button"
                class="subscription-plans-filter-tab"
                :class="{ active: activeVisibilityFilter === 'all' }"
                :aria-pressed="activeVisibilityFilter === 'all'"
                @click="activeVisibilityFilter = 'all'"
              >
                {{ tt("subscriptionPlansAdmin.visibilityAll", "Todos") }}
              </button>
              <button
                type="button"
                class="subscription-plans-filter-tab"
                :class="{ active: activeVisibilityFilter === 'selectable' }"
                :aria-pressed="activeVisibilityFilter === 'selectable'"
                @click="activeVisibilityFilter = 'selectable'"
              >
                {{ tt("subscriptionPlansAdmin.visibilitySelectable", "Visibles en checkout") }}
              </button>
              <button
                type="button"
                class="subscription-plans-filter-tab"
                :class="{ active: activeVisibilityFilter === 'hidden' }"
                :aria-pressed="activeVisibilityFilter === 'hidden'"
                @click="activeVisibilityFilter = 'hidden'"
              >
                {{ tt("subscriptionPlansAdmin.visibilityHidden", "Ocultos del checkout") }}
              </button>
            </div>
          </div>
        </div>

        <div v-if="loading" class="subscription-plans-state">
          <i class="ti-reload ti-spin"></i>
          <p>{{ tt("subscriptionPlansAdmin.loading", "Cargando planes...") }}</p>
        </div>

        <div v-else-if="groupedPlans.length === 0" class="subscription-plans-state">
          <i class="ti-layers"></i>
          <p>{{ tt("subscriptionPlansAdmin.empty", "No hay planes disponibles.") }}</p>
          <button class="btn btn-primary btn-sm btn-round mt-3" :disabled="saving" @click="openCreateModal">
            <i class="ti-plus mr-1"></i>
            {{ tt("subscriptionPlansAdmin.createFirst", "Crear primer plan") }}
          </button>
        </div>

        <div v-else>
          <div v-if="filteredGroupedPlans.length === 0" class="subscription-plans-state subscription-plans-state-compact">
            <i class="ti-filter"></i>
            <p>{{ filteredEmptyMessage }}</p>
          </div>

          <div v-else class="subscription-plans-groups">
            <div v-for="group in filteredGroupedPlans" :key="group.plan_code" class="plan-group-card">
              <div class="plan-group-main">
                <div class="plan-group-header d-flex flex-wrap align-items-start justify-content-between">
                  <div>
                    <div class="d-flex align-items-center flex-wrap">
                      <h5 class="plan-name mb-0 mr-2">{{ group.current.display_name || group.plan_code }}</h5>
                      <span class="badge plan-status-badge" :class="statusBadgeClass(group.current)">
                        {{ statusLabel(group.current) }}
                      </span>
                    </div>
                    <div class="plan-meta mt-2">
                      <span class="plan-code">{{ group.plan_code }}</span>
                      <span class="plan-meta-separator">•</span>
                      <span>{{ tt("subscriptionPlansAdmin.version", "Versión") }} {{ group.current.version }}</span>
                      <span class="plan-meta-separator">•</span>
                      <span>{{ formatPeriod(group.current) }}</span>
                    </div>
                  </div>
                  <div class="plan-actions d-flex flex-wrap align-items-center mt-3 mt-md-0">
                    <div class="selectable-toggle-wrapper mr-2">
                      <span class="toggle-label mr-2">{{ tt("subscriptionPlansAdmin.selectable", "Seleccionable") }}</span>
                      <label class="switch mb-0">
                        <input
                          type="checkbox"
                          :checked="!!group.current.is_selectable"
                          :disabled="togglingPlanCode === group.plan_code"
                          @change="toggleSelectable(group, $event)"
                        >
                        <span class="slider round"></span>
                      </label>
                    </div>
                    <button class="btn btn-outline-secondary btn-sm mr-2" @click="toggleHistory(group.plan_code)">
                      <i :class="expandedHistory[group.plan_code] ? 'ti-angle-up' : 'ti-angle-down'" class="mr-1"></i>
                      {{ expandedHistory[group.plan_code] ? tt("subscriptionPlansAdmin.hideHistory", "Ocultar historial") : tt("subscriptionPlansAdmin.showHistory", "Ver historial") }}
                    </button>
                    <button class="btn btn-primary btn-sm" @click="openEditModal(group)">
                      <i class="ti-pencil mr-1"></i>
                      {{ tt("subscriptionPlansAdmin.edit", "Editar") }}
                    </button>
                  </div>
                </div>

                <div class="table-responsive subscription-plans-table-responsive">
                  <table class="table subscription-plans-table mb-0">
                    <thead>
                      <tr>
                        <th>{{ tt("subscriptionPlansAdmin.plan", "Plan") }}</th>
                        <th>{{ tt("subscriptionPlansAdmin.version", "Versión") }}</th>
                        <th>{{ tt("subscriptionPlansAdmin.creditsPerPeriod", "Créditos/período") }}</th>
                        <th>{{ tt("subscriptionPlansAdmin.period", "Período") }}</th>
                        <th>{{ tt("subscriptionPlansAdmin.priceOriginal", "Precio original") }}</th>
                        <th>{{ tt("subscriptionPlansAdmin.priceLanding", "Precio landing") }}</th>
                        <th>{{ tt("subscriptionPlansAdmin.priceApp", "Precio app") }}</th>
                        <th>{{ tt("subscriptionPlansAdmin.currency", "Moneda") }}</th>
                        <th>{{ tt("subscriptionPlansAdmin.selectable", "Seleccionable") }}</th>
                        <th>{{ tt("subscriptionPlansAdmin.effectiveFrom", "Vigente desde") }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>
                          <div class="font-weight-bold">{{ group.current.display_name || group.plan_code }}</div>
                          <small class="text-muted">{{ group.current.description || tt("subscriptionPlansAdmin.noDescription", "Sin descripción") }}</small>
                        </td>
                        <td>{{ group.current.version }}</td>
                        <td>{{ formatCredits(group.current) }}</td>
                        <td>{{ formatPeriod(group.current) }}</td>
                        <td>{{ formatMoney(group.current.price?.original, group.current.price?.currency || group.current.currency) }}</td>
                        <td>{{ formatMoney(group.current.price?.landing, group.current.price?.currency || group.current.currency) }}</td>
                        <td>{{ formatMoney(group.current.price?.app, group.current.price?.currency || group.current.currency) }}</td>
                        <td>{{ group.current.price?.currency || group.current.currency || "USD" }}</td>
                        <td>
                          <span class="badge" :class="group.current.is_selectable ? 'badge-success' : 'badge-warning'">
                            {{ group.current.is_selectable ? tt("subscriptionPlansAdmin.yes", "Sí") : tt("subscriptionPlansAdmin.no", "No") }}
                          </span>
                        </td>
                        <td>{{ formatDateTime(group.current.effective_from) }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div v-if="expandedHistory[group.plan_code]" class="plan-history-section">
                  <div class="plan-history-title">
                    {{ tt("subscriptionPlansAdmin.versionHistory", "Historial de versiones") }}
                  </div>
                  <div v-if="group.history.length === 0" class="plan-history-empty">
                    {{ tt("subscriptionPlansAdmin.noHistory", "No hay versiones anteriores.") }}
                  </div>
                  <div v-else class="table-responsive">
                    <table class="table subscription-history-table mb-0">
                      <thead>
                        <tr>
                          <th>{{ tt("subscriptionPlansAdmin.version", "Versión") }}</th>
                          <th>{{ tt("subscriptionPlansAdmin.displayName", "Nombre") }}</th>
                          <th>{{ tt("subscriptionPlansAdmin.effectiveFrom", "Vigente desde") }}</th>
                          <th>{{ tt("subscriptionPlansAdmin.effectiveTo", "Vigente hasta") }}</th>
                          <th>{{ tt("subscriptionPlansAdmin.priceOriginal", "Precio original") }}</th>
                          <th>{{ tt("subscriptionPlansAdmin.priceLanding", "Precio landing") }}</th>
                          <th>{{ tt("subscriptionPlansAdmin.priceApp", "Precio app") }}</th>
                          <th>{{ tt("subscriptionPlansAdmin.selectable", "Seleccionable") }}</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="historyPlan in group.history" :key="historyPlan.id">
                          <td>{{ historyPlan.version }}</td>
                          <td>{{ historyPlan.display_name || historyPlan.plan_code }}</td>
                          <td>{{ formatDateTime(historyPlan.effective_from) }}</td>
                          <td>{{ formatDateTime(historyPlan.effective_to) }}</td>
                          <td>{{ formatMoney(historyPlan.price?.original, historyPlan.price?.currency || historyPlan.currency) }}</td>
                          <td>{{ formatMoney(historyPlan.price?.landing, historyPlan.price?.currency || historyPlan.currency) }}</td>
                          <td>{{ formatMoney(historyPlan.price?.app, historyPlan.price?.currency || historyPlan.currency) }}</td>
                          <td>{{ historyPlan.is_selectable ? tt("subscriptionPlansAdmin.yes", "Sí") : tt("subscriptionPlansAdmin.no", "No") }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </card>

    <div
      class="modal fade"
      :class="{ show: showEditModal }"
      :style="{ display: showEditModal ? 'block' : 'none' }"
      tabindex="-1"
      role="dialog"
      @click.self="closeEditModal"
    >
      <div class="modal-dialog modal-lg" role="document">
        <div class="modal-content subscription-plan-modal-content">
          <div class="modal-header subscription-plan-modal-header">
            <h5 class="modal-title">
              {{ modalTitle }}
            </h5>
            <button type="button" class="close" @click="closeEditModal">
              <span>&times;</span>
            </button>
          </div>
          <div class="modal-body subscription-plan-modal-body">
            <div class="alert alert-info mb-4">
              {{ modalNotice }}
            </div>

            <div v-if="modalErrorMessage" class="alert alert-danger">
              {{ modalErrorMessage }}
            </div>

            <form @submit.prevent="submitPlan" class="subscription-plan-form">
              <div class="form-row">
                <div class="form-group" :class="isCreateMode ? 'col-md-12' : 'col-md-6'">
                  <label>{{ tt("subscriptionPlansAdmin.planCode", "Código interno del plan") }}</label>
                  <input
                    v-model.trim="editForm.plan_code"
                    type="text"
                    class="form-control border-input plan-code-input"
                    :readonly="!isCreateMode"
                    :placeholder="tt('subscriptionPlansAdmin.planCodePlaceholder', 'pro_monthly_plus')"
                  >
                  <small class="form-text text-muted">
                    {{ isCreateMode
                      ? tt("subscriptionPlansAdmin.planCodeHelpCreate", "Identificador único usado por backend. Usa letras, números, guion y guion bajo.")
                      : tt("subscriptionPlansAdmin.planCodeHelpLocked", "El código interno no cambia al crear nuevas versiones.") }}
                  </small>
                </div>
                <div v-if="!isCreateMode" class="form-group col-md-6">
                  <label>{{ tt("subscriptionPlansAdmin.currentAction", "Acción") }}</label>
                  <input
                    :value="tt('subscriptionPlansAdmin.newVersion', 'Nueva versión')"
                    type="text"
                    class="form-control border-input"
                    readonly
                  >
                </div>
              </div>

              <div class="form-row">
                <div class="form-group col-md-6">
                  <label>{{ tt("subscriptionPlansAdmin.displayName", "Nombre") }}</label>
                  <input v-model.trim="editForm.display_name" type="text" class="form-control border-input">
                </div>
                <div class="form-group col-md-6">
                  <label>{{ tt("subscriptionPlansAdmin.currency", "Moneda") }}</label>
                  <input v-model.trim="editForm.currency" type="text" maxlength="3" class="form-control border-input">
                </div>
              </div>

              <div class="form-group">
                <label>{{ tt("subscriptionPlansAdmin.description", "Descripción") }}</label>
                <textarea v-model.trim="editForm.description" class="form-control border-input" rows="3" maxlength="500"></textarea>
              </div>

              <div class="form-row">
                <div class="form-group col-md-4">
                  <label>{{ tt("subscriptionPlansAdmin.creditsPerPeriod", "Créditos/período") }}</label>
                  <input v-model.number="editForm.credits_per_period" type="number" min="1" step="1" class="form-control border-input">
                </div>
                <div class="form-group col-md-4">
                  <label>{{ tt("subscriptionPlansAdmin.periodUnit", "Unidad de período") }}</label>
                  <select v-model="editForm.period_unit" class="form-control border-input">
                    <option value="month">{{ tt("subscriptionPlansAdmin.month", "Mes") }}</option>
                    <option value="year">{{ tt("subscriptionPlansAdmin.year", "Año") }}</option>
                  </select>
                </div>
                <div class="form-group col-md-4">
                  <label>{{ tt("subscriptionPlansAdmin.periodAmount", "Cantidad de períodos") }}</label>
                  <input v-model.number="editForm.period_amount" type="number" min="1" step="1" class="form-control border-input">
                </div>
              </div>

              <div class="form-row">
                <div class="form-group col-md-4">
                  <label>{{ tt("subscriptionPlansAdmin.priceOriginalMarketing", "Precio original / marketing") }}</label>
                  <input v-model.number="editForm.price_original" type="number" min="0" step="0.01" class="form-control border-input">
                </div>
                <div class="form-group col-md-4">
                  <label>{{ tt("subscriptionPlansAdmin.priceLandingPage", "Precio landing page") }}</label>
                  <input v-model.number="editForm.price_landing" type="number" min="0" step="0.01" class="form-control border-input">
                </div>
                <div class="form-group col-md-4">
                  <label>{{ tt("subscriptionPlansAdmin.priceAppBilling", "Precio en app / facturación real") }}</label>
                  <input v-model.number="editForm.price_app" type="number" min="0" step="0.01" class="form-control border-input">
                </div>
              </div>

              <div class="form-group mb-0">
                <label class="subscription-checkbox-label">
                  <input v-model="editForm.is_selectable" type="checkbox" class="mr-2">
                  {{ tt("subscriptionPlansAdmin.availableOnCheckout", "Disponible para selección en checkout") }}
                </label>
              </div>
            </form>
          </div>
          <div class="modal-footer subscription-plan-modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeEditModal">
              {{ tt("subscriptionPlansAdmin.cancel", "Cancelar") }}
            </button>
            <button type="button" class="btn btn-primary" :disabled="saving" @click="submitPlan">
              {{ saving ? tt("subscriptionPlansAdmin.saving", "Guardando...") : modalSaveLabel }}
            </button>
          </div>
        </div>
      </div>
    </div>
    <div v-if="showEditModal" class="modal-backdrop fade show" @click="closeEditModal"></div>

    <div
      class="modal fade"
      :class="{ show: showToggleConfirmModal }"
      :style="{ display: showToggleConfirmModal ? 'block' : 'none' }"
      tabindex="-1"
      role="dialog"
      @click.self="cancelToggleSelectable"
    >
      <div class="modal-dialog" role="document">
        <div class="modal-content subscription-plan-modal-content">
          <div class="modal-header subscription-plan-modal-header">
            <h5 class="modal-title">
              {{ tt("subscriptionPlansAdmin.confirmToggleTitle", "Confirmar cambio") }}
            </h5>
            <button type="button" class="close" @click="cancelToggleSelectable">
              <span>&times;</span>
            </button>
          </div>
          <div class="modal-body subscription-plan-modal-body">
            <p class="mb-0">{{ toggleConfirmMessage }}</p>
          </div>
          <div class="modal-footer subscription-plan-modal-footer">
            <button type="button" class="btn btn-secondary" @click="cancelToggleSelectable">
              {{ tt("subscriptionPlansAdmin.cancel", "Cancelar") }}
            </button>
            <button type="button" class="btn btn-primary" :disabled="togglingPlanCode !== ''" @click="confirmToggleSelectable">
              {{ tt("subscriptionPlansAdmin.confirm", "Confirmar") }}
            </button>
          </div>
        </div>
      </div>
    </div>
    <div v-if="showToggleConfirmModal" class="modal-backdrop fade show" @click="cancelToggleSelectable"></div>
  </div>
</template>

<script>
import { Card } from "@/components";
import { supabase } from "@/supabase";
import { useCurrentUserStore } from "@/stores/currentUser";
import { getApiUrl as resolveApiUrl } from "@/api/client";

function buildDefaultForm() {
  return {
    plan_code: "",
    display_name: "",
    description: "",
    credits_per_period: 1,
    period_unit: "month",
    period_amount: 1,
    price_original: 0,
    price_landing: 0,
    price_app: 0,
    currency: "USD",
    is_selectable: true,
  };
}

export default {
  name: "SubscriptionPlansAdmin",
  components: { Card },
  data() {
    return {
      loading: false,
      saving: false,
      activePeriodFilter: "all",
      activeVisibilityFilter: "all",
      modalMode: "edit",
      togglingPlanCode: "",
      errorMessage: "",
      successMessage: "",
      modalErrorMessage: "",
      plans: [],
      expandedHistory: {},
      showEditModal: false,
      showToggleConfirmModal: false,
      pendingTogglePlanCode: "",
      pendingToggleNextValue: null,
      activePlanDisplayName: "",
      editForm: buildDefaultForm(),
      isAdmin: false,
      isTwitchUser: false,
    };
  },
  computed: {
    groupedPlans() {
      const byPlanCode = this.plans.reduce((accumulator, plan) => {
        const key = plan?.plan_code || "unknown";
        if (!accumulator[key]) accumulator[key] = [];
        accumulator[key].push(plan);
        return accumulator;
      }, {});

      return Object.keys(byPlanCode)
        .sort((left, right) => left.localeCompare(right))
        .map((planCode) => {
          const versions = [...byPlanCode[planCode]].sort((left, right) => {
            const leftVersion = Number(left?.version || 0);
            const rightVersion = Number(right?.version || 0);
            if (leftVersion !== rightVersion) return rightVersion - leftVersion;
            return this.toTimestamp(right?.created_at) - this.toTimestamp(left?.created_at);
          });
          const current = versions.find((plan) => plan?.is_active) || versions[0];
          const history = versions.filter((plan) => plan.id !== current?.id);
          return {
            plan_code: planCode,
            current,
            history,
          };
        });
    },
    filteredGroupedPlans() {
      return this.groupedPlans.filter((group) => {
        const plan = group?.current || {};
        const periodUnit = String(plan?.period_unit || "month");
        const isSelectable = !!plan?.is_selectable;

        const matchesPeriod = this.activePeriodFilter === "all"
          ? true
          : periodUnit === (this.activePeriodFilter === "yearly" ? "year" : "month");

        const matchesVisibility = this.activeVisibilityFilter === "all"
          ? true
          : this.activeVisibilityFilter === "selectable"
            ? isSelectable
            : !isSelectable;

        return matchesPeriod && matchesVisibility;
      });
    },
    filteredEmptyMessage() {
      if (this.activeVisibilityFilter === "selectable" && this.activePeriodFilter === "monthly") {
        return this.tt("subscriptionPlansAdmin.emptyMonthlySelectable", "No hay planes mensuales visibles en checkout.");
      }
      if (this.activeVisibilityFilter === "selectable" && this.activePeriodFilter === "yearly") {
        return this.tt("subscriptionPlansAdmin.emptyYearlySelectable", "No hay planes anuales visibles en checkout.");
      }
      if (this.activeVisibilityFilter === "hidden" && this.activePeriodFilter === "monthly") {
        return this.tt("subscriptionPlansAdmin.emptyMonthlyHidden", "No hay planes mensuales ocultos del checkout.");
      }
      if (this.activeVisibilityFilter === "hidden" && this.activePeriodFilter === "yearly") {
        return this.tt("subscriptionPlansAdmin.emptyYearlyHidden", "No hay planes anuales ocultos del checkout.");
      }
      if (this.activeVisibilityFilter === "selectable") {
        return this.tt("subscriptionPlansAdmin.emptySelectable", "No hay planes visibles en checkout.");
      }
      if (this.activeVisibilityFilter === "hidden") {
        return this.tt("subscriptionPlansAdmin.emptyHidden", "No hay planes ocultos del checkout.");
      }
      if (this.activePeriodFilter === "monthly") {
        return this.tt("subscriptionPlansAdmin.emptyMonthly", "No hay planes mensuales disponibles.");
      }
      if (this.activePeriodFilter === "yearly") {
        return this.tt("subscriptionPlansAdmin.emptyYearly", "No hay planes anuales disponibles.");
      }
      return this.tt("subscriptionPlansAdmin.empty", "No hay planes disponibles.");
    },
    toggleConfirmMessage() {
      return this.pendingToggleNextValue
        ? this.tt("subscriptionPlansAdmin.confirmEnableSelectable", "¿Habilitar este plan para selección en checkout?")
        : this.tt("subscriptionPlansAdmin.confirmDisableSelectable", "¿Ocultar este plan del checkout?");
    },
    isCreateMode() {
      return this.modalMode === "create";
    },
    modalTitle() {
      if (this.isCreateMode) {
        return this.tt("subscriptionPlansAdmin.createModalTitle", "Crear plan nuevo");
      }
      return `${this.tt("subscriptionPlansAdmin.editModalTitle", "Editar plan")}: ${this.editForm.display_name || this.activePlanDisplayName} - ${this.tt("subscriptionPlansAdmin.newVersion", "Nueva versión")}`;
    },
    modalNotice() {
      return this.isCreateMode
        ? this.tt("subscriptionPlansAdmin.createNotice", "Guardar registrará un plan nuevo y lo dejará disponible para futuras suscripciones según su configuración.")
        : this.tt("subscriptionPlansAdmin.immutableNotice", "Guardar creará una nueva versión inmutable del plan. Las suscripciones activas conservan los términos anteriores.");
    },
    modalSaveLabel() {
      return this.isCreateMode
        ? this.tt("subscriptionPlansAdmin.saveNewPlan", "Crear plan")
        : this.tt("subscriptionPlansAdmin.saveNewVersion", "Guardar nueva versión");
    },
  },
  async mounted() {
    await this.checkAdminAccess();
    if (this.isAdmin) {
      await this.loadPlans();
    }
  },
  methods: {
    tt(key, fallback) {
      return typeof this.$t === "function" ? this.$t(key) : fallback;
    },
    getApiUrl() {
      return resolveApiUrl();
    },
    async getSessionOrFail() {
      const {
        data: { session },
        error,
      } = await supabase.auth.getSession();
      if (error || !session || !session.access_token) {
        throw new Error(this.tt("subscriptionPlansAdmin.authRequired", "Debes iniciar sesión para ver esta sección."));
      }
      return session;
    },
    async checkAdminAccess() {
      try {
        const session = await this.getSessionOrFail();
        const currentUserStore = useCurrentUserStore();
        await currentUserStore.ensureSessionReady(session);
        if (!currentUserStore.hasAdminAccess) {
          this.$router.push("/create-clips");
          return;
        }
        this.isAdmin = true;
        this.isTwitchUser = currentUserStore.isTwitchUser;
      } catch (error) {
        console.error("Error checking subscription plans admin access:", error);
        this.$router.push("/create-clips");
      }
    },
    toTimestamp(value) {
      const ts = Date.parse(String(value || ""));
      return Number.isNaN(ts) ? 0 : ts;
    },
    clearMessages() {
      this.errorMessage = "";
      this.successMessage = "";
    },
    async loadPlans() {
      this.loading = true;
      this.clearMessages();
      try {
        const session = await this.getSessionOrFail();
        const response = await fetch(`${this.getApiUrl()}/momo/ecartpay/admin/subscription-plans`, {
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
        });
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        const body = await response.json();
        this.plans = Array.isArray(body?.plans) ? body.plans : [];
      } catch (error) {
        console.error("Error loading subscription plans:", error);
        this.errorMessage = error.message || this.tt("subscriptionPlansAdmin.loadError", "Error al cargar planes");
        this.plans = [];
      } finally {
        this.loading = false;
      }
    },
    formatDateTime(value) {
      if (!value) return "-";
      try {
        return new Date(value).toLocaleString();
      } catch (error) {
        return String(value);
      }
    },
    formatCredits(plan) {
      const credits = Number(plan?.credits ?? plan?.credits_per_period ?? 0);
      if (!Number.isFinite(credits)) return "-";
      return credits.toLocaleString();
    },
    formatPeriod(plan) {
      const amount = Number(plan?.period_amount || 1);
      const unit = String(plan?.period_unit || "month");
      const monthLabel = amount === 1 ? this.tt("subscriptionPlansAdmin.month", "Mes") : this.tt("subscriptionPlansAdmin.months", "Meses");
      const yearLabel = amount === 1 ? this.tt("subscriptionPlansAdmin.year", "Año") : this.tt("subscriptionPlansAdmin.years", "Años");
      return `${amount} ${unit === "year" ? yearLabel : monthLabel}`;
    },
    formatMoney(value, currency = "USD") {
      const amount = Number(value);
      if (!Number.isFinite(amount)) return "-";
      try {
        return new Intl.NumberFormat(undefined, {
          style: "currency",
          currency: currency || "USD",
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(amount);
      } catch (error) {
        return `${amount.toFixed(2)} ${currency || "USD"}`;
      }
    },
    statusBadgeClass(plan) {
      if (plan?.is_active && plan?.is_selectable) return "badge-success";
      if (plan?.is_active && !plan?.is_selectable) return "badge-warning";
      return "badge-secondary";
    },
    statusLabel(plan) {
      if (plan?.is_active && plan?.is_selectable) return this.tt("subscriptionPlansAdmin.statusActiveSelectable", "Activo y seleccionable");
      if (plan?.is_active && !plan?.is_selectable) return this.tt("subscriptionPlansAdmin.statusActiveHidden", "Activo pero oculto");
      return this.tt("subscriptionPlansAdmin.statusInactive", "Inactivo");
    },
    toggleHistory(planCode) {
      this.$set(this.expandedHistory, planCode, !this.expandedHistory[planCode]);
    },
    buildEditFormFromPlan(plan) {
      return {
        plan_code: plan?.plan_code || "",
        display_name: plan?.display_name || "",
        description: plan?.description || "",
        credits_per_period: Number(plan?.credits ?? plan?.credits_per_period ?? 1),
        period_unit: plan?.period_unit || "month",
        period_amount: Number(plan?.period_amount || 1),
        price_original: Number(plan?.price?.original ?? 0),
        price_landing: Number(plan?.price?.landing ?? 0),
        price_app: Number(plan?.price?.app ?? 0),
        currency: String(plan?.price?.currency || plan?.currency || "USD").toUpperCase(),
        is_selectable: !!plan?.is_selectable,
      };
    },
    openCreateModal() {
      this.modalMode = "create";
      this.modalErrorMessage = "";
      this.activePlanDisplayName = "";
      this.editForm = buildDefaultForm();
      this.showEditModal = true;
      document.body.style.overflow = "hidden";
    },
    openEditModal(group) {
      this.modalMode = "edit";
      this.modalErrorMessage = "";
      this.activePlanDisplayName = group?.current?.display_name || group?.plan_code || "";
      this.editForm = this.buildEditFormFromPlan(group?.current || {});
      this.showEditModal = true;
      document.body.style.overflow = "hidden";
    },
    closeEditModal() {
      this.showEditModal = false;
      this.modalMode = "edit";
      this.modalErrorMessage = "";
      this.editForm = buildDefaultForm();
      document.body.style.overflow = "";
    },
    openToggleConfirmModal(group, nextValue) {
      this.pendingTogglePlanCode = group?.plan_code || "";
      this.pendingToggleNextValue = !!nextValue;
      this.showToggleConfirmModal = true;
      document.body.style.overflow = "hidden";
    },
    closeToggleConfirmModal() {
      this.showToggleConfirmModal = false;
      this.pendingTogglePlanCode = "";
      this.pendingToggleNextValue = null;
      if (!this.showEditModal) {
        document.body.style.overflow = "";
      }
    },
    cancelToggleSelectable() {
      this.closeToggleConfirmModal();
    },
    validateEditForm() {
      const planCode = String(this.editForm.plan_code || "").trim();
      const displayName = String(this.editForm.display_name || "").trim();
      const description = String(this.editForm.description || "");
      const currency = String(this.editForm.currency || "").trim().toUpperCase();
      const credits = Number(this.editForm.credits_per_period);
      const periodAmount = Number(this.editForm.period_amount);
      const priceOriginal = Number(this.editForm.price_original);
      const priceLanding = Number(this.editForm.price_landing);
      const priceApp = Number(this.editForm.price_app);

      if (planCode.length < 2 || planCode.length > 80) {
        return this.tt("subscriptionPlansAdmin.validationPlanCodeLength", "El código interno debe tener entre 2 y 80 caracteres.");
      }
      if (!/^[A-Za-z0-9_-]+$/.test(planCode)) {
        return this.tt("subscriptionPlansAdmin.validationPlanCodeFormat", "El código interno solo puede contener letras, números, guion y guion bajo.");
      }
      if (displayName.length < 2 || displayName.length > 120) {
        return this.tt("subscriptionPlansAdmin.validationDisplayName", "El nombre debe tener entre 2 y 120 caracteres.");
      }
      if (description.length > 500) {
        return this.tt("subscriptionPlansAdmin.validationDescription", "La descripción no puede exceder 500 caracteres.");
      }
      if (!Number.isInteger(credits) || credits <= 0) {
        return this.tt("subscriptionPlansAdmin.validationCredits", "Los créditos por período deben ser un entero mayor a 0.");
      }
      if (!["month", "year"].includes(this.editForm.period_unit)) {
        return this.tt("subscriptionPlansAdmin.validationPeriodUnit", "La unidad de período es inválida.");
      }
      if (!Number.isInteger(periodAmount) || periodAmount <= 0) {
        return this.tt("subscriptionPlansAdmin.validationPeriodAmount", "La cantidad de períodos debe ser un entero mayor a 0.");
      }
      if (![priceOriginal, priceLanding, priceApp].every((value) => Number.isFinite(value) && value >= 0)) {
        return this.tt("subscriptionPlansAdmin.validationPrices", "Los precios deben ser números mayores o iguales a 0.");
      }
      if (currency.length !== 3) {
        return this.tt("subscriptionPlansAdmin.validationCurrency", "La moneda debe tener exactamente 3 caracteres.");
      }
      return "";
    },
    async submitPlan() {
      const validationError = this.validateEditForm();
      if (validationError) {
        this.modalErrorMessage = validationError;
        return;
      }

      this.saving = true;
      this.modalErrorMessage = "";
      this.errorMessage = "";
      this.successMessage = "";

      try {
        const session = await this.getSessionOrFail();
        const normalizedPlanCode = String(this.editForm.plan_code || "").trim();
        const payload = {
          display_name: String(this.editForm.display_name || "").trim(),
          description: String(this.editForm.description || "").trim(),
          credits_per_period: Number(this.editForm.credits_per_period),
          period_unit: this.editForm.period_unit,
          period_amount: Number(this.editForm.period_amount),
          price_original: Number(this.editForm.price_original),
          price_landing: Number(this.editForm.price_landing),
          price_app: Number(this.editForm.price_app),
          currency: String(this.editForm.currency || "USD").trim().toUpperCase(),
          is_selectable: !!this.editForm.is_selectable,
        };

        const response = await fetch(`${this.getApiUrl()}/momo/ecartpay/admin/subscription-plans/${encodeURIComponent(normalizedPlanCode)}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify(payload),
        });

        const body = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(body?.detail || body?.message || `HTTP ${response.status}`);
        }

        this.successMessage = body?.message || (this.isCreateMode
          ? this.tt("subscriptionPlansAdmin.createSuccess", "Se creó el plan correctamente.")
          : this.tt("subscriptionPlansAdmin.updateSuccess", "Se creó una nueva versión del plan correctamente."));
        if (typeof this.$notify === "function") {
          this.$notify({
            message: this.successMessage,
            type: "success",
          });
        }
        this.closeEditModal();
        await this.loadPlans();
      } catch (error) {
        console.error(this.isCreateMode ? "Error creating subscription plan:" : "Error creating new plan version:", error);
        this.modalErrorMessage = error.message || (this.isCreateMode
          ? this.tt("subscriptionPlansAdmin.createError", "No se pudo crear el plan.")
          : this.tt("subscriptionPlansAdmin.updateError", "No se pudo guardar la nueva versión."));
      } finally {
        this.saving = false;
      }
    },
    async confirmToggleSelectable() {
      if (!this.pendingTogglePlanCode) return;
      const planCode = this.pendingTogglePlanCode;
      const nextValue = !!this.pendingToggleNextValue;

      this.togglingPlanCode = planCode;
      this.errorMessage = "";
      this.successMessage = "";
      try {
        const session = await this.getSessionOrFail();
        const response = await fetch(`${this.getApiUrl()}/momo/ecartpay/admin/subscription-plans/${encodeURIComponent(planCode)}/selectable`, {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ is_selectable: nextValue }),
        });
        const body = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(body?.detail || body?.message || `HTTP ${response.status}`);
        }
        this.successMessage = this.tt("subscriptionPlansAdmin.toggleSuccess", "Disponibilidad actualizada correctamente.");
        if (typeof this.$notify === "function") {
          this.$notify({
            message: this.successMessage,
            type: "success",
          });
        }
        this.closeToggleConfirmModal();
        await this.loadPlans();
      } catch (error) {
        console.error("Error toggling selectable plan:", error);
        this.errorMessage = error.message || this.tt("subscriptionPlansAdmin.toggleError", "No se pudo actualizar la disponibilidad.");
      } finally {
        this.togglingPlanCode = "";
      }
    },
    toggleSelectable(group, event) {
      const nextValue = !!event?.target?.checked;
      if (event?.target) {
        event.target.checked = !!group?.current?.is_selectable;
      }
      this.openToggleConfirmModal(group, nextValue);
    },
  },
  beforeDestroy() {
    document.body.style.overflow = "";
  },
};
</script>

<style scoped>
.subscription-plans-header {
  gap: 1rem;
}

.subscription-plans-header-actions {
  gap: 0.65rem;
}

.subscription-plans-subtitle {
  color: #64748b;
  font-size: 0.95rem;
}

.subscription-plans-body {
  padding: 0 1.5rem 1.5rem;
}

.subscription-plans-filters {
  display: grid;
  gap: 1rem;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  align-items: start;
}

.subscription-plans-filter-group {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.subscription-plans-filters-label {
  color: #475569;
  font-size: 0.9rem;
  font-weight: 600;
}

.subscription-plans-filter-tabs {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.subscription-plans-filter-tab {
  border: 1px solid #cbd5e1;
  background: #ffffff;
  color: #475569;
  border-radius: 999px;
  padding: 0.45rem 0.95rem;
  font-size: 0.85rem;
  font-weight: 700;
  transition: all 0.2s ease;
}

.subscription-plans-filter-tab:hover {
  border-color: #94a3b8;
  color: #0f172a;
}

.subscription-plans-filter-tab.active {
  background: #0f172a;
  border-color: #0f172a;
  color: #ffffff;
}

.subscription-plans-state {
  min-height: 240px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: #64748b;
}

.subscription-plans-state-compact {
  min-height: 180px;
}

.subscription-plans-state i {
  font-size: 2.5rem;
  margin-bottom: 0.75rem;
}

.subscription-plans-groups {
  display: grid;
  gap: 1rem;
}

.plan-group-card {
  border: 1px solid #e2e8f0;
  border-radius: 18px;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  overflow: hidden;
}

.plan-group-main {
  padding: 1.25rem;
}

.plan-name {
  font-size: 1.2rem;
  font-weight: 700;
  color: #0f172a;
}

.plan-status-badge {
  font-size: 0.72rem;
}

.plan-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem;
  color: #64748b;
  font-size: 0.9rem;
}

.plan-meta-separator {
  color: #94a3b8;
}

.plan-code {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.85rem;
}

.plan-actions {
  gap: 0.6rem;
}

.toggle-label {
  font-size: 0.85rem;
  color: #475569;
  font-weight: 600;
}

.subscription-plans-table-responsive,
.plan-history-section {
  margin-top: 1rem;
}

.subscription-plans-table thead th,
.subscription-history-table thead th {
  border-top: none;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #64748b;
}

.subscription-plans-table tbody td,
.subscription-history-table tbody td {
  vertical-align: middle;
}

.plan-history-section {
  background: rgba(241, 245, 249, 0.7);
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 1rem;
}

.plan-history-title {
  font-weight: 700;
  color: #334155;
  margin-bottom: 0.75rem;
}

.plan-history-empty {
  color: #64748b;
}

.subscription-plan-modal-content {
  border-radius: 18px;
  overflow: hidden;
}

.subscription-plan-modal-header,
.subscription-plan-modal-footer {
  border-color: #e2e8f0;
}

.subscription-plan-modal-body {
  max-height: 75vh;
  overflow-y: auto;
}

.subscription-plan-form label {
  font-weight: 600;
  color: #334155;
}

.plan-code-input {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
}

.subscription-checkbox-label {
  display: inline-flex;
  align-items: center;
}

.modal-title-separator {
  color: #94a3b8;
  margin: 0 0.35rem;
}

.switch {
  position: relative;
  display: inline-block;
  width: 44px;
  height: 24px;
}

.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #cbd5e1;
  transition: 0.2s;
}

.slider:before {
  position: absolute;
  content: "";
  height: 18px;
  width: 18px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  transition: 0.2s;
}

input:checked + .slider {
  background-color: #22c55e;
}

input:focus + .slider {
  box-shadow: 0 0 1px #22c55e;
}

input:checked + .slider:before {
  transform: translateX(20px);
}

.slider.round {
  border-radius: 24px;
}

.slider.round:before {
  border-radius: 50%;
}

.subscription-plans-admin-page.twitch-theme .plan-group-card,
.subscription-plans-admin-page.twitch-theme :deep(.card),
.subscription-plans-admin-page.twitch-theme :deep(.modal-content) {
  background: var(--clipr-bg-card) !important;
  border-color: var(--clipr-border-color) !important;
  color: var(--clipr-text-primary) !important;
}

.subscription-plans-admin-page.twitch-theme .subscription-plans-subtitle,
.subscription-plans-admin-page.twitch-theme .plan-meta,
.subscription-plans-admin-page.twitch-theme .toggle-label,
.subscription-plans-admin-page.twitch-theme .plan-history-empty,
.subscription-plans-admin-page.twitch-theme .subscription-plans-filters-label {
  color: var(--clipr-text-secondary) !important;
}

.subscription-plans-admin-page.twitch-theme .subscription-plans-filter-tab {
  background: transparent;
  border-color: var(--clipr-border-color) !important;
  color: var(--clipr-text-secondary) !important;
}

.subscription-plans-admin-page.twitch-theme .subscription-plans-filter-tab.active {
  background: var(--clipr-accent, #22c55e) !important;
  border-color: var(--clipr-accent, #22c55e) !important;
  color: #04130b !important;
}

.subscription-plans-admin-page.twitch-theme .plan-name,
.subscription-plans-admin-page.twitch-theme .plan-history-title,
.subscription-plans-admin-page.twitch-theme .subscription-plan-form label,
.subscription-plans-admin-page.twitch-theme :deep(.modal-title) {
  color: var(--clipr-text-primary) !important;
}

.subscription-plans-admin-page.twitch-theme .plan-history-section,
.subscription-plans-admin-page.twitch-theme :deep(.form-control),
.subscription-plans-admin-page.twitch-theme :deep(.border-input) {
  background: var(--clipr-bg-elevated) !important;
  border-color: var(--clipr-border-color) !important;
  color: var(--clipr-text-primary) !important;
}

.subscription-plans-admin-page.twitch-theme .subscription-plans-table thead th,
.subscription-plans-admin-page.twitch-theme .subscription-history-table thead th {
  color: var(--clipr-text-secondary) !important;
}

.subscription-plans-admin-page.twitch-theme .subscription-plans-table tbody td,
.subscription-plans-admin-page.twitch-theme .subscription-history-table tbody td,
.subscription-plans-admin-page.twitch-theme .subscription-plans-table tbody td span,
.subscription-plans-admin-page.twitch-theme .subscription-history-table tbody td span {
  color: var(--clipr-text-primary) !important;
}

.subscription-plans-admin-page.twitch-theme .subscription-plans-table tbody td small,
.subscription-plans-admin-page.twitch-theme .subscription-history-table tbody td small,
.subscription-plans-admin-page.twitch-theme .subscription-plans-table .text-muted,
.subscription-plans-admin-page.twitch-theme .subscription-history-table .text-muted {
  color: var(--clipr-text-secondary) !important;
  opacity: 1 !important;
}

@media (max-width: 767.98px) {
  .subscription-plans-body {
    padding: 0 1rem 1rem;
  }

  .plan-group-main {
    padding: 1rem;
  }

  .subscription-plans-header-actions {
    width: 100%;
  }

  .subscription-plans-filters {
    grid-template-columns: 1fr;
  }

  .subscription-plans-filter-tabs {
    width: 100%;
  }

  .subscription-plans-filter-tab {
    flex: 1 1 auto;
    text-align: center;
  }

  .plan-actions {
    width: 100%;
    align-items: stretch;
  }

  .selectable-toggle-wrapper {
    width: 100%;
    justify-content: space-between;
    display: flex;
    align-items: center;
    margin-right: 0 !important;
  }
}
</style>